'use memo';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { AlertCard } from '@/components/AlertCard';
import { VStack } from '@/components/Layout';
import { ViewportLoader } from '@/components/Loading';
import { toast } from '@/components/ui/use-toast';
import { accessRulesKeyPrefix, authPoliciesKeyPrefix, toAccessRuleFormValue, useAccessRule, useAuthenticationPolicyList, useSoftwareNameLookup, useUpdateAccessRule } from '@/features/authentication-policy';
import { PolicySectionHeader } from '@/features/identity-policy';
import { handleApiError } from '@/lib/api/error';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { AccessRuleFormScreen } from '../-components/AccessRuleFormScreen';
export const Route = createFileRoute('/_staff/settings/authentication/_layout/access-rules/$id/')({
  component: EditAccessRulePage
});
function EditAccessRulePage() {
  const {
    id
  } = Route.useParams();
  const {
    i18n,
    t
  } = useTranslation(defaultNS, {
    keyPrefix: accessRulesKeyPrefix
  });
  const {
    t: tPolicies
  } = useTranslation(defaultNS, {
    keyPrefix: authPoliciesKeyPrefix
  });
  const navigate = useNavigate();
  const {
    data,
    isPending,
    isError
  } = useAccessRule(id);
  // ポリシー選択肢を親で待ち、AccessRuleFormScreen の二重 ViewportLoader を避ける
  const policyQuery = useAuthenticationPolicyList();
  const softwareIds = data?.applicationScope ?? [];
  const {
    resolveSoftwareName,
    isPending: isSoftwareLookupPending,
    isError: isSoftwareLookupError
  } = useSoftwareNameLookup(softwareIds);
  const updateMutation = useUpdateAccessRule();
  const [errorMessage, setErrorMessage] = useState('');
  const backToList = () => navigate({
    to: '/settings/authentication/access-rules'
  });

  // 名前解決前にフォームを初期化すると ID が焼き付くため、必要な取得が揃うまで待つ
  if (isPending || isSoftwareLookupPending || policyQuery.isPending) {
    return <ViewportLoader />;
  }
  if (isError || data == null) {
    return <VStack className="w-full gap-4 px-12 py-6">
        <PolicySectionHeader iconName="ShieldIcon" title={t($ => $.form.editTitle)} description={t($ => $.description)} />
        <AlertCard variant="mutedError" description={t($ => $.list.loadError)} />
      </VStack>;
  }
  if (policyQuery.isError || policyQuery.data == null) {
    return <VStack className="w-full gap-4 px-12 py-6">
        <PolicySectionHeader iconName="ShieldIcon" title={t($ => $.form.editTitle)} description={t($ => $.description)} />
        <AlertCard variant="mutedError" description={tPolicies($ => $.list.loadError)} />
      </VStack>;
  }
  return <VStack className="w-full gap-4">
      {isSoftwareLookupError && <VStack className="px-12 pt-6">
          <AlertCard variant="mutedError" description={t($ => $.form.softwareNameLoadError)} />
        </VStack>}
      <AccessRuleFormScreen
    // 取得後に一度だけフォームを初期化する（再取得で編集内容を巻き戻さない）
    key={data.id} title={t($ => $.form.editTitle)} initialValue={toAccessRuleFormValue(data, resolveSoftwareName)} isSubmitting={updateMutation.isPending} errorMessage={errorMessage} onCancel={() => void backToList()} onSubmit={draft => {
      setErrorMessage('');
      updateMutation.mutate({
        ...draft,
        id
      }, {
        onSuccess: () => {
          toast({
            description: t($ => $.toast.updated)
          });
          void backToList();
        },
        onError: error => {
          setErrorMessage(handleApiError(error, {
            language: i18n.language
          }).message);
        }
      });
    }} />
    </VStack>;
}